/**
 * Shared mDNS gateway claim beacon.
 *
 * One claim code and one beacon per browser session, running at one of two
 * cadences:
 *
 * - **background** (60s) — only ever when Local Network Access is already
 *   granted. Costs the user nothing they haven't agreed to, and lets us notice
 *   a gateway that gets plugged in while they're on some other page.
 * - **foreground** (5s) — while the scan panel is open and the user is actively
 *   waiting. The panel "boosts" the same beacon rather than starting a second
 *   one, so there's still a single claim code and a single Redis session.
 *
 * Nothing beacons until either the permission is granted or the user explicitly
 * clicks Scan. Beaconing on load is what raised an uncontextualised Chrome
 * permission prompt on whatever page the user happened to open.
 */

import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  beaconClaimCode,
  createClaimSession,
  listAvailableGateways,
  ClaimSessionExpiredError,
  AvailableGatewayInfo,
} from '../services/gatewayClaim';
import { useClaimCodeSubscription } from './useClaimCodeSubscription';
import {
  useLocalNetworkPermission,
  type LocalNetworkPermissionState,
} from './useLocalNetworkPermission';

const FOREGROUND_INTERVAL_MS = 5_000;
const BACKGROUND_INTERVAL_MS = 60_000;

/**
 * How long an explicit scan runs before giving up. The old code beaconed every
 * 5s forever; a bounded scan with a "Scan again" button is both cheaper and
 * more honest about the fact that nothing is showing up.
 */
const FOREGROUND_MAX_MS = 120_000;

/**
 * How long before a claim session lapses to treat it as already gone.
 *
 * Without a margin the session can expire between the check and the beacon it
 * guards, which is the same failure one round later. Wide enough to cover the
 * round trip that renews it.
 */
const SESSION_RENEW_MARGIN_MS = 30_000;

/**
 * Fallback lifetime when the backend's `expiresAt` will not parse. Shorter than
 * the real TTL on purpose: renewing early costs one cheap call, while assuming
 * too much is the bug this whole mechanism exists to prevent.
 */
const ASSUMED_SESSION_TTL_MS = 120_000;

/**
 * Floor on a session's assumed lifetime. Must stay above
 * `SESSION_RENEW_MARGIN_MS`, or a session would be retired the instant it is
 * registered and renewal would spin.
 */
const MIN_SESSION_LIFETIME_MS = 60_000;

/** Ceiling on a session's assumed lifetime, so a bad value cannot park one open. */
const MAX_SESSION_LIFETIME_MS = 600_000;

/**
 * Remembers that the user has explicitly scanned in this browser.
 *
 * Only consulted where the browser has no Local Network Access permission to
 * read (Firefox, Safari today). There, the click *is* the consent record —
 * without banking it, a gesture requirement with no way to satisfy it durably
 * means those browsers never get the background watch at all.
 */
const SCAN_CONSENT_KEY = 'netman.localNetworkScan.consented';

function readScanConsent(): boolean {
  try {
    return window.localStorage.getItem(SCAN_CONSENT_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeScanConsent(consented: boolean): void {
  try {
    if (consented) window.localStorage.setItem(SCAN_CONSENT_KEY, 'true');
    else window.localStorage.removeItem(SCAN_CONSENT_KEY);
  } catch {
    // Private mode / quota. Costs the background watch, nothing else.
  }
}

/**
 * Generate a random claim code (lowercase for consistency with backend normalization)
 */
function generateClaimCode(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let code = '';
  for (let i = 0; i < 12; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

export type ScanMode = 'off' | 'background' | 'foreground';

export interface UseGatewayClaimBeaconResult {
  claimCode: string;
  sessionRegistered: boolean;
  permission: LocalNetworkPermissionState;
  mode: ScanMode;
  /** An explicit user-initiated scan is running. */
  isForegroundScanning: boolean;
  /** The 60s background watch is live. */
  isWatching: boolean;
  /**
   * The background watch is running off a remembered click rather than a
   * browser permission — so we own the off switch, and have to offer one.
   */
  isConsentWatch: boolean;
  /** Forget the remembered click and stop the background watch. */
  stopWatching: () => void;
  /** The last explicit scan hit its time limit without being stopped. */
  scanTimedOut: boolean;
  /** Wall-clock ms since the current foreground scan began, or null. */
  scanElapsedMs: number | null;
  discoveredGateways: AvailableGatewayInfo[];
  isLoading: boolean;
  error: Error | null;
  sessionError: Error | null;
  /** Pre-register the claim session so a later Scan click can beacon at once. */
  arm: () => void;
  /** Begin an explicit scan. Safe to call when the permission is still 'prompt' — that's what raises it. */
  startScan: () => void;
  stopScan: () => void;
  refetch: () => Promise<unknown>;
}

export function useGatewayClaimBeacon(enabled = true): UseGatewayClaimBeaconResult {
  const [claimCode] = useState(generateClaimCode);
  // When the backend says this session lapses, as epoch ms — not a "registered"
  // boolean. The boolean was a claim about *server* state that nothing could
  // observe expiring: it latched true on first registration and was never
  // cleared, so after the Redis key aged out (CLAIM_SESSION_TTL, 5 min) a Scan
  // click skipped re-registration and beaconed a code the backend would 404.
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);
  const sessionRegistered = sessionExpiresAt !== null;
  const [sessionError, setSessionError] = useState<Error | null>(null);
  const [armed, setArmed] = useState(false);
  const [foregroundStartedAt, setForegroundStartedAt] = useState<number | null>(null);
  const [scanTimedOut, setScanTimedOut] = useState(false);
  /** When the scan clock last ticked; elapsed time derives from it below. */
  const [scanTickAt, setScanTickAt] = useState<number | null>(null);
  const [visible, setVisible] = useState(() =>
    typeof document === 'undefined' ? true : !document.hidden,
  );

  const [scanConsent, setScanConsent] = useState(readScanConsent);

  const { state: permission } = useLocalNetworkPermission();

  const isForegroundScanning = foregroundStartedAt !== null;

  // Where the browser gates local network access, the permission is the only
  // thing worth trusting: a remembered click must never re-arm a background
  // watch in a browser that would answer it with a prompt every 60 seconds.
  // Where there is no such permission, nothing is gated and the click is the
  // only consent signal that exists — so honour it.
  const isConsentWatch = permission === 'unsupported' && scanConsent;
  const backgroundActive = enabled && (permission === 'granted' || isConsentWatch);
  // A foreground scan is the user standing there waiting, so it wins on cadence.
  const mode: ScanMode = !enabled
    ? 'off'
    : isForegroundScanning
      ? 'foreground'
      : backgroundActive
        ? 'background'
        : 'off';
  const intervalMs = mode === 'foreground' ? FOREGROUND_INTERVAL_MS : BACKGROUND_INTERVAL_MS;
  const scanning = mode !== 'off' && visible;

  useClaimCodeSubscription(claimCode, sessionRegistered);

  // Track tab visibility. A beacon aimed at a tab nobody is looking at helps
  // no one, and pausing here is also what keeps multiple open tabs from all
  // beaconing at once — only the visible one runs.
  useEffect(() => {
    const onVisibility = () => { setVisible(!document.hidden); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { document.removeEventListener('visibilitychange', onVisibility); };
  }, []);

  // Register the claim session once we have a reason to: either the permission
  // is already granted (background watch is about to run) or the scan panel
  // armed us. Registering is a plain authenticated API call with no permission
  // implications, so doing it ahead of the click keeps the click itself free to
  // beacon immediately.
  const shouldRegister = enabled && (armed || backgroundActive);
  useEffect(() => {
    if (!shouldRegister || sessionRegistered) return;

    const unmounted = new AbortController();
    void (async () => {
      try {
        const session = await createClaimSession(claimCode);
        if (unmounted.signal.aborted) return;
        // Registering the same code again is safe and is how the session is
        // renewed: the backend SET NXs, and for the same owner falls through to
        // refreshing the TTL.
        // Read the server's timestamp as a *lifetime* and re-anchor it to the
        // local clock. An absolute instant from another machine is only as good
        // as the agreement between two clocks, and a browser running ahead
        // would see a fresh session as already lapsed — then clear it, register
        // again, and spin. Clamped so neither a skewed clock nor a surprising
        // TTL can drive renewal faster than the margin or stretch it past sense.
        const parsed = Date.parse(session.expiresAt);
        const remaining = Number.isNaN(parsed) ? ASSUMED_SESSION_TTL_MS : parsed - Date.now();
        const lifetime = Math.min(
          Math.max(remaining, MIN_SESSION_LIFETIME_MS),
          MAX_SESSION_LIFETIME_MS,
        );
        setSessionExpiresAt(Date.now() + lifetime);
        setSessionError(null);
      } catch (err) {
        if (unmounted.signal.aborted) return;
        setSessionError(
          err instanceof Error ? err : new Error('Failed to register claim session'),
        );
      }
    })();

    return () => { unmounted.abort(); };
  }, [shouldRegister, sessionRegistered, claimCode]);

  // Retire the session a margin before the backend does. Clearing it is what
  // re-opens the effect above, so while there is still a reason to hold one
  // (armed, or the background watch is running) this renews; once there isn't,
  // it simply lapses. Either way nothing downstream beacons a dead code.
  //
  // A backgrounded tab has its timers throttled, so this can fire late and the
  // session read as live past its expiry. That is what the 404 handler below is
  // for: the server is the authority, this is only the cheap common path.
  useEffect(() => {
    if (sessionExpiresAt === null) return;

    // Already due fires on the next tick, from the timer like the rest.
    const dueIn = Math.max(0, sessionExpiresAt - SESSION_RENEW_MARGIN_MS - Date.now());
    const id = window.setTimeout(() => { setSessionExpiresAt(null); }, dueIn);
    return () => { window.clearTimeout(id); };
  }, [sessionExpiresAt]);

  // The beacon itself.
  useEffect(() => {
    if (!scanning || !sessionRegistered) return;

    void beaconClaimCode(claimCode);
    const id = window.setInterval(() => { void beaconClaimCode(claimCode); }, intervalMs);
    return () => { window.clearInterval(id); };
  }, [scanning, sessionRegistered, claimCode, intervalMs]);

  // A denial mid-scan ends the scan. Every subsequent beacon would be refused
  // at the browser, and the panel has already swapped to its blocked state —
  // leaving the timer running just burns the rest of the two minutes.
  if (permission === 'denied' && foregroundStartedAt !== null) {
    setForegroundStartedAt(null);
  }

  // Bound the explicit scan and keep a ticking elapsed time for the UI.
  useEffect(() => {
    if (foregroundStartedAt === null) return;

    const tick = () => {
      const now = Date.now();
      if (now - foregroundStartedAt >= FOREGROUND_MAX_MS) {
        setForegroundStartedAt(null);
        setScanTimedOut(true);
        return;
      }
      setScanTickAt(now);
    };

    tick();
    const id = window.setInterval(tick, 1_000);
    return () => { window.clearInterval(id); };
  }, [foregroundStartedAt]);
  // A tick left over from an earlier scan predates this one's start, so it reads as 0
  // rather than as the last scan's time.
  const scanElapsedMs =
    foregroundStartedAt === null
      ? null
      : Math.max(0, (scanTickAt ?? foregroundStartedAt) - foregroundStartedAt);

  const arm = useCallback(() => { setArmed(true); }, []);

  const startScan = useCallback(() => {
    setArmed(true);
    setScanTimedOut(false);
    setForegroundStartedAt(Date.now());
    // Bank the gesture. Only load-bearing in browsers with no permission to
    // read; elsewhere `isConsentWatch` ignores it in favour of the real answer.
    setScanConsent(true);
    writeScanConsent(true);
    // Fire one beacon straight from the click when the session is genuinely
    // still good. Chrome's prompt isn't documented to need transient
    // activation, but keeping the local-network request inside the gesture
    // costs nothing and makes the prompt feel attached to the button.
    //
    // Read the clock here rather than trusting the rendered flag: the click is
    // exactly when a throttled renew timer would not yet have fired, and
    // beaconing a lapsed code is the bug. If it has lapsed, drop the session so
    // the registration effect renews it and the beacon effect fires the moment
    // it lands — a beat later, but against a code the backend will accept.
    const stillFresh =
      sessionExpiresAt !== null && sessionExpiresAt - Date.now() > SESSION_RENEW_MARGIN_MS;
    if (stillFresh) {
      void beaconClaimCode(claimCode);
    } else {
      setSessionExpiresAt(null);
    }
  }, [claimCode, sessionExpiresAt]);

  const stopScan = useCallback(() => {
    setForegroundStartedAt(null);
    setScanTimedOut(false);
  }, []);

  const stopWatching = useCallback(() => {
    setForegroundStartedAt(null);
    setScanConsent(false);
    writeScanConsent(false);
  }, []);

  const {
    data: discoveredGateways = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['available-gateways', claimCode],
    queryFn: () => listAvailableGateways(claimCode),
    enabled: sessionRegistered && scanning,
    refetchInterval: intervalMs,
    retry: false,
  });

  // The server is the authority on whether the session still exists. This poll
  // doubles as the backend's TTL heartbeat, so it is also the first thing to
  // notice when the key is gone for a reason no local timer predicts — evicted,
  // flushed, or lost to a failover. Drop the session and let it re-register.
  const sessionGone = error instanceof ClaimSessionExpiredError;
  // Only on the transition: the error stays cached while a replacement session
  // registers, and clearing on every render would drop that one too.
  const [sawSessionGone, setSawSessionGone] = useState(false);
  if (sessionGone !== sawSessionGone) {
    setSawSessionGone(sessionGone);
    if (sessionGone) setSessionExpiresAt(null);
  }

  return {
    claimCode,
    sessionRegistered,
    permission,
    mode,
    isForegroundScanning,
    isWatching: backgroundActive,
    isConsentWatch,
    stopWatching,
    scanTimedOut,
    scanElapsedMs,
    discoveredGateways,
    isLoading,
    // An expired session is handled, not shown: the effect above has already
    // dropped it and registration is on its way. Surfacing it would put a
    // failure in front of the user for the one case that repairs itself.
    error: sessionGone ? null : error,
    sessionError,
    arm,
    startScan,
    stopScan,
    refetch,
  };
}
